import {
  applyTimeIncrement,
  buildIncrementWarning,
  formatDuration,
  formatIncrementShort,
  hoursToMinutes,
} from '@packages/types';

/**
 * Off-increment steering for a duration the user is currently typing.
 *
 * Gated on the same `buildIncrementWarning` predicate the backend uses for the
 * `warnings` array on its create/update responses, so the UI and the API can
 * never disagree about WHEN a duration is off-increment. The UI renders its
 * own terse copy because it has affordances the API string cannot carry (a
 * settings deep link and a round-up action).
 *
 * Rounding is offered, never applied: the returned inputs are what a one-click
 * round-up would fill in. The user's own entry is what gets saved unless they
 * take the action.
 */
export interface IncrementSteering {
  /** Short label for the configured increment, e.g. "6 min". */
  incrementLabel: string;
  /** Hours field value after rounding up. */
  roundedHoursInput: string;
  /** Minutes field value after rounding up. */
  roundedMinutesInput: string;
  /** Full-words duration after rounding up, e.g. "1 hour 30 minutes". */
  roundedLabel: string;
}

/**
 * Returns the steering for `totalMinutes` against the account's increment, or
 * null when it already lines up, the account has no increment configured, or
 * the entry carries no duration at all.
 */
export function buildIncrementSteering(
  totalMinutes: number,
  incrementHours: number | null,
): IncrementSteering | null {
  if (!incrementHours) return null;
  if (totalMinutes <= 0) return null;

  const rawUnits = totalMinutes / 60;
  if (!buildIncrementWarning(rawUnits, incrementHours)) return null;

  const roundedUnits = applyTimeIncrement(rawUnits, incrementHours);
  const roundedTotalMinutes = hoursToMinutes(roundedUnits);
  return {
    incrementLabel: formatIncrementShort(incrementHours),
    roundedHoursInput: String(Math.floor(roundedTotalMinutes / 60)),
    roundedMinutesInput: String(roundedTotalMinutes % 60),
    roundedLabel: formatDuration(roundedUnits),
  };
}

/** Parses the hours + minutes text inputs into a total minute count. */
export function totalMinutesFromInputs(hoursInput: string, minutesInput: string): number {
  const hours = hoursInput ? parseInt(hoursInput, 10) : 0;
  const minutes = minutesInput ? parseInt(minutesInput, 10) : 0;
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;
  return hours * 60 + minutes;
}
